import {
  AlertCircle,
  Building2,
  Calendar,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Flag,
  MapPin,
  Repeat,
  Search,
  Users,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip } from 'recharts';
import SingleLocationSelector from '../components/SingleLocationSelector';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Table } from '../components/ui/Table';
import { Pagination } from '../components/ui/Pagination';
import { Skeleton } from '../components/ui/Skeleton';
import useLocale from '../contexts/LocaleContext';
import { useAssessments, useUnassessedFacilities } from '../hooks/useAssessments';
import { getCurrentUser } from '../hooks/useAuth';
import { useFacilityTypes } from '../hooks/useFacilities';
import { assessmentsApi, questionsApi, usersApi } from '../lib/api-client';
import { canExportData } from '../lib/permissions';
import { getLocationLevelName } from '../lib/utils';
import type { AssessmentDto, HygieneQuestionnaireListItemDto, UserDto } from '../types/api';

type AssessmentTab = 'assessed' | 'unassessed';
type PeriodType = 'week' | 'month' | 'dateRange';

interface Filters {
  // Period
  periodType: PeriodType;
  weekNumber?: number;
  year?: number;
  month?: number;
  monthYear?: number;
  startDate?: string;
  endDate?: string;
  // Server-side filters
  locationId?: string;
  facilityId?: string;
  userIds?: string[];
  facilityTypeId?: string;
  questionnaireId?: string;
  assessmentType?: 'HYGIENE' | 'COMPLIANCE';
  // Client-side only
  isClean?: boolean;
  minScore?: number;
  maxScore?: number;
}

interface WeekData {
  week: number;
  year: number;
  start: Date;
  end: Date;
}

// Popover Component
interface CommitteePopoverProps {
  committee: string[];
  children: React.ReactNode;
}

const CommitteePopover = ({ committee, children }: CommitteePopoverProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Parse committee member string: "John Niyontwali (Software Engineer) - 0786266073"
  const parseCommitteeMember = (memberStr: string) => {
    const match = memberStr.match(/^(.+?)\s*\((.+?)\)\s*-\s*(.+)$/);
    if (match) {
      return {
        name: match[1].trim(),
        position: match[2].trim(),
        phone: match[3].trim(),
      };
    }
    return { name: memberStr, position: '', phone: '' };
  };

  return (
    <div className='relative inline-block'>
      <div
        ref={triggerRef}
        onClick={() => setIsVisible(!isVisible)}
        className='cursor-pointer hover:text-primary transition-colors inline-flex items-center gap-1'
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={popoverRef}
          className='fixed z-30 min-w-[280px] max-w-[320px] bg-white border border-gray-200 rounded-lg shadow-2xl'
          style={{
            left: triggerRef.current ? `${triggerRef.current.getBoundingClientRect().left}px` : '0',
            bottom: triggerRef.current
              ? `${window.innerHeight - triggerRef.current.getBoundingClientRect().top + 8}px`
              : 'auto',
          }}
        >
          {/* Arrow pointing down */}
          <div className='absolute w-3 h-3 bg-white border-r border-b border-gray-200 transform rotate-45 -bottom-[6px] left-4' />

          {/* Popover Content */}
          <div className='relative z-10 p-4'>
            <div className='mb-4 pb-3 border-b border-gray-200'>
              <h4 className='font-semibold text-gray-900 text-base mb-1'>Committee Members</h4>
              <p className='text-xs text-gray-500'>
                {committee?.length || 0} {committee?.length === 1 ? 'member' : 'members'}
              </p>
            </div>

            <div className='space-y-3'>
              {committee && committee.length > 0 ? (
                <div className='space-y-2 max-h-[240px] overflow-y-auto'>
                  {committee.map((memberStr, index) => {
                    const member = parseCommitteeMember(memberStr);
                    return (
                      <div key={index} className='flex items-start gap-3'>
                        <div className='flex-shrink-0 w-6 h-6 bg-secondary rounded-full flex items-center justify-center'>
                          <span className='text-xs font-medium text-primary'>
                            {member.name
                              .split(' ')
                              .map(n => n[0])
                              .join('')
                              .toUpperCase()
                              .slice(0, 2)}
                          </span>
                        </div>
                        <div className='flex-1 min-w-0'>
                          <div className='text-sm font-medium text-gray-900 truncate'>{member.name}</div>
                          {member.position && <div className='text-xs text-gray-600 truncate'>{member.position}</div>}
                          {member.phone && <div className='text-xs text-gray-500 truncate'>{member.phone}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className='text-sm text-gray-500 text-center py-2'>No members found</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Fixed week calculation functions
const getCurrentWeekAndYear = (): { week: number; year: number } => {
  const now = new Date();

  // Clone the date to avoid modifying the original
  const date = new Date(now.getTime());

  // ISO week date: weeks start on Monday
  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  const day = date.getDay() || 7; // Convert Sunday (0) to 7
  date.setDate(date.getDate() + 4 - day);

  // Get first day of year
  const yearStart = new Date(date.getFullYear(), 0, 1);

  // Calculate full weeks to nearest Thursday
  const weekNumber = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

  return { week: weekNumber, year: date.getFullYear() };
};

const getWeekDateRange = (week: number, year: number): { start: Date; end: Date } => {
  // Create a date for January 1st of the year
  const date = new Date(year, 0, 1);

  // Get the day of the week for January 1st (0 = Sunday, 1 = Monday, etc.)
  const dayOfWeek = date.getDay();

  // Calculate the start of the first week (Monday)
  // If Jan 1 is Friday, Saturday, or Sunday, it belongs to the last week of previous year
  let startOfFirstWeek = new Date(date);

  // Adjust to the previous Monday if needed
  if (dayOfWeek <= 4) {
    // Monday through Thursday
    startOfFirstWeek.setDate(date.getDate() - (dayOfWeek - 1));
  } else {
    // Friday through Sunday - move to next Monday
    startOfFirstWeek.setDate(date.getDate() + (8 - dayOfWeek));
  }

  // Calculate the start of the requested week
  const startOfWeek = new Date(startOfFirstWeek);
  startOfWeek.setDate(startOfFirstWeek.getDate() + (week - 1) * 7);

  // Calculate the end of the week (Sunday)
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return { start: startOfWeek, end: endOfWeek };
};

const formatDateRange = (start: Date, end: Date) => {
  const startDay = start.getDate();
  const endDay = end.getDate();
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const year = end.getFullYear();

  if (startMonth === endMonth) {
    return `${startDay}–${endDay} ${startMonth} ${year}`;
  }
  return `${startDay} ${startMonth}–${endDay} ${endMonth} ${year}`;
};

const getScoreColorClass = (score: number) => {
  if (score >= 70) return 'text-green-600 bg-green-50 border border-green-200';
  if (score >= 50) return 'text-yellow-600 bg-yellow-50 border border-yellow-200';
  return 'text-red-600 bg-red-50 border border-red-200';
};

const getFacilityTypeName = (facilityType: any, locale: string) => {
  if (!facilityType) return '';
  if (locale === 'rw') return facilityType.nameKinyarwanda;
  if (locale === 'fr') return facilityType.nameFrench;
  return facilityType.name;
};

// Helper function to determine if facility is clean based on percentage score
const isCleanFacility = (assessment: AssessmentDto): boolean => {
  // Calculate clean status: >= 50% is clean, < 50% is not clean
  return assessment.percentageScore >= 50;
};

// Draft detection utilities
const DRAFT_STORAGE_KEY_PREFIX = 'nhip_assessment_draft_';
const DRAFT_MAX_AGE_DAYS = 7;

const getDraftStorageKey = (userId: string) => `${DRAFT_STORAGE_KEY_PREFIX}${userId}`;

const checkForDraft = (userId: string): boolean => {
  try {
    const key = getDraftStorageKey(userId);
    const draftStr = localStorage.getItem(key);
    if (!draftStr) return false;

    const draft = JSON.parse(draftStr);

    // Check if draft belongs to current user
    if (draft.userId !== userId) return false;

    // Check if draft is not too old (7 days)
    const ageInDays = (Date.now() - draft.timestamp) / (1000 * 60 * 60 * 24);
    if (ageInDays > DRAFT_MAX_AGE_DAYS) {
      localStorage.removeItem(key);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Failed to check for draft:', error);
    return false;
  }
};

const getDraftTimestamp = (userId: string): number | null => {
  try {
    const key = getDraftStorageKey(userId);
    const draftStr = localStorage.getItem(key);
    if (!draftStr) return null;
    const draft = JSON.parse(draftStr);
    return draft.timestamp;
  } catch (error) {
    return null;
  }
};

// ─── Multi-user conductor picker ────────────────────────────────────────────
interface UserMultiSelectProps {
  selectedIds: string[];
  selectedUsers: UserDto[];
  onChange: (ids: string[], users: UserDto[]) => void;
}

function UserMultiSelect({ selectedIds, selectedUsers, onChange }: UserMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Position the dropdown using fixed coords so it escapes overflow:hidden ancestors
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 6, left: rect.left });
    }
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on scroll/resize so the dropdown doesn't drift, but ignore
  // scroll events that originate inside the dropdown itself
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const { data: results, isFetching } = useQuery({
    queryKey: ['users-conductor-search', debouncedSearch],
    queryFn: () => usersApi.getAll({ search: debouncedSearch || undefined, status: 'ACTIVE', limit: 30 }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const toggle = (user: UserDto) => {
    if (selectedIds.includes(user.id)) {
      onChange(selectedIds.filter(id => id !== user.id), selectedUsers.filter(u => u.id !== user.id));
    } else {
      onChange([...selectedIds, user.id], [...selectedUsers, user]);
    }
  };

  const clearAll = () => onChange([], []);

  const hasSelection = selectedIds.length > 0;

  return (
    <div className='flex-shrink-0'>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type='button'
        onClick={() => setOpen(p => !p)}
        className={`w-[170px] flex-shrink-0 h-[46px] px-5 text-sm border-2 rounded-lg flex items-center gap-1.5 bg-white transition-colors justify-between ${
          hasSelection ? 'border-primary text-primary bg-secondary/40' : 'border-slate-100 text-gray-600 hover:border-slate-300'
        }`}
      >
        <span className='flex items-center gap-1.5 truncate min-w-0'>
          <Users className='h-3.5 w-3.5 flex-shrink-0' />
          <span className='truncate'>{hasSelection ? `${selectedIds.length} conductor${selectedIds.length > 1 ? 's' : ''}` : 'Any conductor'}</span>
        </span>
        <ChevronDown className={`h-3 w-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown — rendered via fixed position to escape overflow:hidden */}
      {open && dropdownPos && (
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: 288 }}
          className='bg-white border-2 border-slate-100 rounded-lg shadow-lg z-[9999] overflow-hidden'
        >
          {/* Search header */}
          <div className='p-2.5 border-b border-gray-100'>
            <Input
              ref={inputRef}
              type='text'
              placeholder='Search by name or email…'
              value={search}
              onChange={e => setSearch(e.target.value)}
              inputPrefix={<Search className='h-3.5 w-3.5 ml-3 text-gray-400 flex-shrink-0' />}
              className='text-sm'
            />
            {hasSelection && (
              <button
                type='button'
                onClick={clearAll}
                className='mt-1.5 text-xs text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors'
              >
                <X className='h-3 w-3' /> Clear all ({selectedIds.length})
              </button>
            )}
          </div>

          {/* Results */}
          <div className='max-h-60 overflow-y-auto'>
            {isFetching && !results ? (
              <div className='flex justify-center py-5'>
                <div className='h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin' />
              </div>
            ) : !results?.data?.length ? (
              <p className='text-xs text-gray-400 text-center py-5'>No users found</p>
            ) : (
              results.data.map(user => {
                const selected = selectedIds.includes(user.id);
                return (
                  <button
                    key={user.id}
                    type='button'
                    onClick={() => toggle(user)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                      selected ? 'bg-secondary/60' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${
                      selected ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='text-sm font-medium text-gray-900 truncate'>{user.name}</div>
                      <div className='text-xs text-gray-400 truncate'>{user.email}</div>
                    </div>
                    {selected && <Check className='h-4 w-4 text-primary flex-shrink-0' />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Inline chips row for selected conductors ────────────────────────────────
function ConductorChips({ users, onRemove }: { users: UserDto[]; onRemove: (id: string) => void }) {
  if (!users.length) return null;
  return (
    <div className='flex flex-wrap gap-1 px-4 pb-2'>
      {users.map(u => (
        <span key={u.id} className='inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-secondary text-primary text-xs rounded-full border border-primary/20 font-medium'>
          <span className='w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0'>
            {u.name.charAt(0).toUpperCase()}
          </span>
          {u.name}
          <button type='button' onClick={() => onRemove(u.id)} className='ml-0.5 hover:bg-primary/10 rounded-full p-0.5 transition-colors'>
            <X className='h-2.5 w-2.5' />
          </button>
        </span>
      ))}
    </div>
  );
}

export default function AssessmentsList() {
  const { dictionary, locale } = useLocale();
  const currentUser = getCurrentUser();
  const currentWeekData = getCurrentWeekAndYear();

  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<AssessmentTab>('assessed');
  const [locationResetKey, setLocationResetKey] = useState(Date.now());
  const [isExporting, setIsExporting] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftTimestamp, setDraftTimestamp] = useState<number | null>(null);

  const now = new Date();
  const [filters, setFilters] = useState<Filters>({
    periodType: 'week',
    weekNumber: currentWeekData.week,
    year: currentWeekData.year,
    month: now.getMonth() + 1,
    monthYear: now.getFullYear(),
    startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [unassessedCurrentPage, setUnassessedCurrentPage] = useState(1);
  const [unassessedItemsPerPage, setUnassessedItemsPerPage] = useState(10);
  // Conductor multi-select: store full user objects so we can render chips
  const [selectedConductors, setSelectedConductors] = useState<UserDto[]>([]);

  const weekScrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedWeekRef = useRef<HTMLButtonElement>(null);

  // Debounce search term to avoid too many API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when debounced search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const assessmentDateParams = useMemo(() => {
    if (filters.periodType === 'week') {
      return { weekNumber: filters.weekNumber, year: filters.year };
    }
    if (filters.periodType === 'month' && filters.month != null && filters.monthYear != null) {
      const start = new Date(filters.monthYear, filters.month - 1, 1);
      const end = new Date(filters.monthYear, filters.month, 0);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      };
    }
    if (filters.periodType === 'dateRange' && filters.startDate && filters.endDate) {
      return { startDate: filters.startDate, endDate: filters.endDate };
    }
    return { weekNumber: filters.weekNumber, year: filters.year };
  }, [filters.periodType, filters.weekNumber, filters.year, filters.month, filters.monthYear, filters.startDate, filters.endDate]);

  const { data: assessmentsResponse, isLoading } = useAssessments({
    locationId: filters.locationId,
    facilityId: filters.facilityId,
    userIds: filters.userIds?.length ? filters.userIds : undefined,
    facilityTypeId: filters.facilityTypeId,
    questionnaireId: filters.questionnaireId,
    assessmentType: filters.assessmentType,
    ...assessmentDateParams,
    search: debouncedSearchTerm.trim() || undefined,
    page: currentPage,
    limit: itemsPerPage,
  });

  const assessmentsData = assessmentsResponse?.data || [];
  const totalItems = assessmentsResponse?.pagination?.totalItems || 0;
  const totalPages = assessmentsResponse?.pagination?.totalPages || Math.ceil(totalItems / itemsPerPage);
  const { data: facilityTypes } = useFacilityTypes();

  // Fetch questionnaires for the filter dropdown (scoped by selected facility type)
  const { data: questionnaires } = useQuery<HygieneQuestionnaireListItemDto[]>({
    queryKey: ['hygiene-questionnaires', filters.facilityTypeId],
    queryFn: () => questionsApi.listHygieneQuestionnaires(filters.facilityTypeId),
    enabled: filters.assessmentType !== 'COMPLIANCE',
    staleTime: 1000 * 60 * 5,
  });
  const { data: unassessedData, isLoading: unassessedLoading } = useUnassessedFacilities({
    ...assessmentDateParams,
    locationId: filters.locationId,
    facilityTypeId: filters.facilityTypeId,
    assessmentType: filters.assessmentType,
    page: unassessedCurrentPage,
    limit: unassessedItemsPerPage,
  });

  const canCreateAssessment = currentUser?.role === 'ADMIN' || currentUser?.role === 'PROVINCE_ADMIN' || currentUser?.role === 'DISTRICT_ADMIN' || currentUser?.role === 'SECTOR_ADMIN' || currentUser?.role === 'CELL_ADMIN' || currentUser?.role === 'VILLAGE_ADMIN';

  // Check for draft on component mount
  useEffect(() => {
    if (!currentUser?.id) return;

    const draftExists = checkForDraft(currentUser.id);
    setHasDraft(draftExists);

    if (draftExists) {
      const timestamp = getDraftTimestamp(currentUser.id);
      setDraftTimestamp(timestamp);
    }
  }, [currentUser?.id]);

  // Auto-scroll selected week into view
  useEffect(() => {
    if (selectedWeekRef.current && weekScrollContainerRef.current) {
      selectedWeekRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [filters.weekNumber, filters.year]);

  // Direct export using current filters — assessed tab exports assessments, unassessed tab exports unassessed
  const handleExport = async () => {
    if (!canExportData(currentUser?.role)) {
      toast.error('You do not have permission to export data');
      return;
    }

    const exportParams = {
      ...assessmentDateParams,
      locationId: filters.locationId,
      facilityTypeId: filters.facilityTypeId,
      assessmentType: filters.assessmentType,
    };

    setIsExporting(true);
    try {
      if (activeTab === 'unassessed') {
        await assessmentsApi.exportUnassessedExcel(exportParams);
      } else {
        await assessmentsApi.exportAssessmentsExcel({
          ...exportParams,
          userIds: filters.userIds?.length ? filters.userIds : undefined,
          facilityId: filters.facilityId,
          search: debouncedSearchTerm.trim() || undefined,
        });
      }
      toast.success('Export completed');
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const filteredAssessments: AssessmentDto[] = useMemo(() => {
    let filtered = assessmentsData || [];

    // Client-side only filters (not supported by API)
    if (filters.isClean !== undefined) {
      filtered = filtered.filter(a => isCleanFacility(a) === filters.isClean);
    }
    if (filters.minScore !== undefined && !isNaN(filters.minScore)) {
      filtered = filtered.filter(a => a.percentageScore >= filters.minScore!);
    }
    if (filters.maxScore !== undefined && !isNaN(filters.maxScore)) {
      filtered = filtered.filter(a => a.percentageScore <= filters.maxScore!);
    }

    return filtered;
  }, [assessmentsData, filters.isClean, filters.minScore, filters.maxScore]);

  const facilityTypePerformance = useMemo(() => {
    if (!filteredAssessments.length) return [];

    const typeScores: Record<string, { total: number; count: number; name: string }> = {};

    filteredAssessments.forEach(assessment => {
      const facilityType = assessment.facility?.facilityType;
      if (facilityType) {
        const typeName = getFacilityTypeName(facilityType, locale);

        if (!typeScores[facilityType.id]) {
          typeScores[facilityType.id] = { total: 0, count: 0, name: typeName };
        }
        typeScores[facilityType.id].total += assessment.percentageScore;
        typeScores[facilityType.id].count += 1;
      }
    });

    return Object.values(typeScores).map(data => ({
      facilityType: data.name,
      score: Math.round(data.total / data.count),
      fullMark: 100,
    }));
  }, [filteredAssessments, locale]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value === '' || value === null ? undefined : value }));
    setCurrentPage(1);
    setUnassessedCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters(prev => ({
      ...prev,
      locationId: undefined,
      facilityId: undefined,
      userIds: undefined,
      facilityTypeId: undefined,
      questionnaireId: undefined,
      assessmentType: undefined,
      isClean: undefined,
      minScore: undefined,
      maxScore: undefined,
    }));
    setSelectedConductors([]);
    setSearchTerm('');
    setLocationResetKey(prev => prev + 1);
    setCurrentPage(1);
    setUnassessedCurrentPage(1);
  };

  // Reset questionnaireId when facilityTypeId or assessmentType changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, questionnaireId: undefined }));
  }, [filters.facilityTypeId, filters.assessmentType]);

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.locationId ||
      filters.facilityTypeId ||
      filters.assessmentType ||
      (filters.userIds?.length ?? 0) > 0 ||
      filters.facilityId ||
      filters.isClean !== undefined ||
      filters.minScore !== undefined ||
      filters.maxScore !== undefined ||
      searchTerm.trim().length > 0
    );
  }, [filters, searchTerm]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const generateWeeksTimeline = (): WeekData[] => {
    const weeks: WeekData[] = [];
    const currentWeek = filters.weekNumber || currentWeekData.week;
    const currentYear = filters.year || currentWeekData.year;

    for (let i = -5; i <= 5; i++) {
      let week = currentWeek + i;
      let year = currentYear;

      // Handle week overflow between years
      if (week < 1) {
        year -= 1;
        // Get the total weeks in the previous year
        const lastDayOfPrevYear = new Date(year, 11, 31);
        const lastWeekData = getCurrentWeekAndYear();
        // Simple approximation - most years have 52 weeks, some 53
        week =
          lastDayOfPrevYear.getDay() === 4 ||
          (lastDayOfPrevYear.getDay() === 3 && new Date(year, 11, 31).getDate() === 31)
            ? 53
            : 52;
      } else if (week > 52) {
        // Check if week 53 exists for this year
        const lastDayOfYear = new Date(year, 11, 31);
        const maxWeeks =
          lastDayOfYear.getDay() === 4 || (lastDayOfYear.getDay() === 3 && lastDayOfYear.getDate() === 31) ? 53 : 52;

        if (week > maxWeeks) {
          year += 1;
          week = week - maxWeeks;
        }
      }

      const { start, end } = getWeekDateRange(week, year);
      weeks.push({ week, year, start, end });
    }

    return weeks;
  };

  const weeksTimeline = generateWeeksTimeline();
  const selectedWeek = filters.weekNumber || currentWeekData.week;
  const selectedYear = filters.year || currentWeekData.year;

  // Server-side pagination data for unassessed facilities
  const unassessedFacilities = unassessedData?.facilities || [];
  // Use pagination metadata if available from backend, otherwise fall back to unassessedCount
  const unassessedTotalItems = unassessedData?.total ?? unassessedData?.unassessedCount ?? 0;
  const unassessedTotalPages = Math.ceil(unassessedTotalItems / unassessedItemsPerPage);

  const navigateWeek = (direction: 'prev' | 'next') => {
    let newWeek = selectedWeek + (direction === 'next' ? 1 : -1);
    let newYear = selectedYear;

    // Handle week transitions between years
    if (newWeek < 1) {
      newYear -= 1;
      // Get total weeks in previous year
      const lastDayOfPrevYear = new Date(newYear, 11, 31);
      newWeek =
        lastDayOfPrevYear.getDay() === 4 || (lastDayOfPrevYear.getDay() === 3 && lastDayOfPrevYear.getDate() === 31)
          ? 53
          : 52;
    } else if (newWeek > 52) {
      // Check if week 53 exists for current year
      const lastDayOfYear = new Date(newYear, 11, 31);
      const maxWeeks =
        lastDayOfYear.getDay() === 4 || (lastDayOfYear.getDay() === 3 && lastDayOfYear.getDate() === 31) ? 53 : 52;

      if (newWeek > maxWeeks) {
        newYear += 1;
        newWeek = 1;
      }
    }

    setFilters(prev => ({ ...prev, weekNumber: newWeek, year: newYear }));
  };

  const selectWeek = (week: number, year: number) => {
    setFilters(prev => ({ ...prev, weekNumber: week, year: year }));
    setUnassessedCurrentPage(1);
  };

  const goToCurrentWeek = () => {
    const current = getCurrentWeekAndYear();
    setFilters(prev => ({ ...prev, weekNumber: current.week, year: current.year }));
    setUnassessedCurrentPage(1);
  };

  const selectedWeekData = weeksTimeline.find(w => w.week === selectedWeek && w.year === selectedYear);

  const tableColumns = [
    {
      key: 'facility',
      label: 'Facility',
      sortable: true,
      render: (assessment: AssessmentDto) => (
        <Link to={`/assessments/${assessment.id}`} className='group relative'>
          {/* Desktop tooltip */}
          <div className='hidden sm:block absolute -top-8 left-0 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10'>
            Click for details
          </div>
          <div className='flex items-center gap-3'>
            <div className='bg-secondary rounded-lg p-2 flex-shrink-0'>
              <Building2 className='h-4 w-4 text-primary' />
            </div>
            <div className='flex items-center gap-2 flex-wrap min-w-0'>
              <span className='font-medium text-gray-900 whitespace-nowrap group-hover:text-primary transition-colors'>
                {assessment.facility?.title || dictionary['assessments.unknown_facility']}
              </span>
              {assessment.assessmentType && (
                <span
                  className={`flex-shrink-0 inline-flex px-2 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                    assessment.assessmentType === 'HYGIENE'
                      ? 'bg-secondary text-primary border border-primary/30'
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}
                >
                  {assessment.assessmentType === 'HYGIENE'
                    ? (dictionary['assessment_detail.type_hygiene'] ?? 'Hygiene')
                    : (dictionary['assessment_detail.type_compliance'] ?? 'Compliance')}
                </span>
              )}
              {assessment.isReassessment && (
                <div className='flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 border border-purple-300 rounded-md text-xs font-semibold whitespace-nowrap'>
                  <Repeat className='h-3 w-3' />
                  <span>Reassessment</span>
                </div>
              )}
            </div>
          </div>
        </Link>
      ),
    },
    {
      key: 'facilityType',
      label: 'Type',
      render: (assessment: AssessmentDto) =>
        assessment.facility?.facilityType ? (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 whitespace-nowrap'>
            {getFacilityTypeName(assessment.facility.facilityType, locale)}
          </span>
        ) : (
          <span className='text-gray-400'>—</span>
        ),
    },
    {
      key: 'province',
      label: 'Province',
      render: (assessment: AssessmentDto) => {
        const name = getLocationLevelName(assessment.locationStack?.locationNames, 'PROVINCE', assessment.facility?.location);
        return name ? <span className='text-sm text-gray-600 whitespace-nowrap'>{name}</span> : <span className='text-gray-400'>—</span>;
      },
    },
    {
      key: 'district',
      label: 'District',
      render: (assessment: AssessmentDto) => {
        const name = getLocationLevelName(assessment.locationStack?.locationNames, 'DISTRICT', assessment.facility?.location);
        return name ? <span className='text-sm text-gray-600 whitespace-nowrap'>{name}</span> : <span className='text-gray-400'>—</span>;
      },
    },
    {
      key: 'sector',
      label: 'Sector',
      render: (assessment: AssessmentDto) => {
        const name = getLocationLevelName(assessment.locationStack?.locationNames, 'SECTOR', assessment.facility?.location);
        return name ? <span className='text-sm text-gray-600 whitespace-nowrap'>{name}</span> : <span className='text-gray-400'>—</span>;
      },
    },
    {
      key: 'cell',
      label: 'Cell',
      render: (assessment: AssessmentDto) => {
        const name = getLocationLevelName(assessment.locationStack?.locationNames, 'CELL', assessment.facility?.location);
        return name ? <span className='text-sm text-gray-600 whitespace-nowrap'>{name}</span> : <span className='text-gray-400'>—</span>;
      },
    },
    {
      key: 'village',
      label: 'Village',
      render: (assessment: AssessmentDto) => {
        const name = getLocationLevelName(assessment.locationStack?.locationNames, 'VILLAGE', assessment.facility?.location);
        return name ? <span className='text-sm text-gray-600 whitespace-nowrap'>{name}</span> : <span className='text-gray-400'>—</span>;
      },
    },
    {
      key: 'assessor',
      label: 'Assessed By',
      render: (assessment: AssessmentDto) =>
        assessment?.committee && Array.isArray(assessment.committee) && assessment.committee.length > 0 ? (
          <CommitteePopover committee={assessment.committee}>
            <div className='flex items-center gap-2 text-sm group'>
              <Users className='h-3 w-3 text-gray-400 flex-shrink-0 group-hover:text-primary transition-colors' />
              <span className='truncate group-hover:text-primary transition-colors'>
                {assessment.committee.length} {assessment.committee.length === 1 ? 'member' : 'members'}
              </span>
            </div>
          </CommitteePopover>
        ) : (
          <span className='text-gray-400'>—</span>
        ),
    },
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      render: (assessment: AssessmentDto) => (
        <div className='flex items-center gap-1 text-sm text-gray-600'>
          <Calendar className='h-3 w-3 text-gray-400 flex-shrink-0' />
          <span className='whitespace-nowrap'>{new Date(assessment.createdAt).toLocaleDateString()}</span>
        </div>
      ),
    },
    {
      key: 'flag',
      label: '',
      render: (assessment: AssessmentDto) =>
        assessment.isFlagged ? (
          <span
            className='inline-flex items-center justify-center rounded p-1 bg-red-100 text-red-600'
            title={dictionary['dashboard.flagged'] ?? 'Flagged'}
          >
            <Flag className='h-4 w-4 fill-current' />
          </span>
        ) : (
          <span className='inline-flex items-center justify-center rounded p-1 text-gray-300' aria-hidden>
            <Flag className='h-4 w-4' />
          </span>
        ),
    },
    {
      key: 'score',
      label: 'Score',
      sortable: true,
      render: (assessment: AssessmentDto) => {
        const scoreColor = getScoreColorClass(assessment.percentageScore);
        return (
          <div className={`inline-flex items-center px-3 py-1 rounded-lg font-bold text-sm ${scoreColor}`}>
            {assessment.percentageScore.toFixed(1)}%
          </div>
        );
      },
    },
  ];

  return (
    <div className='p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-[1600px] mx-auto'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
        <div>
          <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>{dictionary['assessments.title']}</h1>
          <p className='text-sm text-gray-600 mt-1'>{dictionary['assessments.view_manage']}</p>
        </div>
        <div className='flex flex-col sm:flex-row gap-2 w-full sm:w-auto'>
          {/* Export Button */}
          <Button
            variant='outline'
            onClick={handleExport}
            disabled={isExporting}
            className='w-full sm:w-auto flex items-center gap-2 text-primary hover:text-primary/80 border-[1px] border-primary/20 bg-secondary hover:bg-secondary'
          >
            <Download className='h-4 w-4' />
            {isExporting ? 'Exporting…' : (dictionary['assessments.export'] || 'Export Excel')}
          </Button>

          {canCreateAssessment && (
            <Link to='/assessments/new' className='w-full sm:w-auto'>
              <Button className='w-full sm:w-auto'>
                <FileText className='h-4 w-4 mr-2' />
                {dictionary['assessments.new_assessment']}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Draft Recovery Banner */}
      {hasDraft && canCreateAssessment && (
        <div className='bg-amber-50 border-l-4 border-amber-400 p-3 sm:p-4 rounded-lg shadow-sm'>
          <div className='flex items-start gap-2 sm:gap-3'>
            <AlertCircle className='h-4 w-4 sm:h-5 sm:w-5 text-amber-600 mt-0.5 flex-shrink-0' />
            <div className='flex-1 min-w-0'>
              <h3 className='text-xs sm:text-sm font-semibold text-amber-900'>
                {dictionary['assessments.draft_found_list']}
              </h3>
              <div className='mt-1 sm:mt-2'>
                <p className='text-xs sm:text-sm text-amber-700 hidden sm:block'>
                  {dictionary['assessments.draft_message']}
                </p>
                <p className='text-xs text-amber-700 sm:hidden'>{dictionary['assessments.draft_message_mobile']}</p>
                {draftTimestamp && (
                  <p className='mt-1 text-xs text-amber-600'>
                    {dictionary['new_assessment.last_saved']}:{' '}
                    <span className='hidden sm:inline'>{new Date(draftTimestamp).toLocaleString()}</span>
                    <span className='sm:hidden'>{new Date(draftTimestamp).toLocaleDateString()}</span>
                  </p>
                )}
              </div>
              <div className='mt-2 sm:mt-3'>
                <Link to='/assessments/new'>
                  <Button size='sm' className='bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs'>
                    <FileText className='h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2' />
                    <span className='hidden sm:inline'>{dictionary['assessments.go_to_draft']}</span>
                    <span className='sm:hidden'>{dictionary['assessments.go_to_draft_mobile']}</span>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unified Filter Card */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>

        {/* Period section */}
        <div className='px-4 pt-3 pb-3'>
          {/* Period tabs + actions on same row */}
          <div className='flex items-center gap-2 mb-3'>
            <div className='flex rounded-lg border border-gray-200 overflow-hidden'>
              {(['week', 'month', 'dateRange'] as const).map(type => (
                <button
                  key={type}
                  type='button'
                  onClick={() => handleFilterChange('periodType', type)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    filters.periodType === type
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-50 bg-white'
                  }`}
                >
                  {type === 'week'
                    ? (dictionary['assessments.by_week'] ?? 'By week')
                    : type === 'month'
                      ? (dictionary['assessments.by_month'] ?? 'By month')
                      : (dictionary['assessments.by_date_range'] ?? 'Date range')}
                </button>
              ))}
            </div>

            {filters.periodType === 'week' && (
              <button onClick={goToCurrentWeek} className='px-2.5 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md border border-gray-200 transition-colors'>
                {dictionary['assessments.current_week']}
              </button>
            )}
            {filters.periodType === 'month' && (
              <button onClick={() => { const n = new Date(); handleFilterChange('month', n.getMonth() + 1); handleFilterChange('monthYear', n.getFullYear()); }} className='px-2.5 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md border border-gray-200 transition-colors'>
                {dictionary['assessments.current_month'] ?? 'Current month'}
              </button>
            )}
          </div>

          {/* Period selector */}
          {filters.periodType === 'week' && (
            <div className='flex items-center gap-1'>
              <button onClick={() => navigateWeek('prev')} className='p-1.5 rounded-md hover:bg-gray-100 flex-shrink-0 text-gray-500' aria-label='Previous week'>
                <ChevronLeft className='h-4 w-4' />
              </button>
              <div ref={weekScrollContainerRef} className='flex-1 overflow-x-auto' style={{ scrollbarWidth: 'none' }}>
                <div className='flex gap-1.5 min-w-max'>
                  {weeksTimeline.map(weekData => {
                    const isSelected = weekData.week === selectedWeek && weekData.year === selectedYear;
                    const isCurrent = weekData.week === currentWeekData.week && weekData.year === currentWeekData.year;
                    return (
                      <button
                        key={`${weekData.year}-${weekData.week}`}
                        ref={isSelected ? selectedWeekRef : null}
                        onClick={() => selectWeek(weekData.week, weekData.year)}
                        className={`flex-shrink-0 rounded-lg px-3 py-1.5 border text-center transition-all ${
                          isSelected
                            ? 'bg-primary text-white border-primary shadow-sm'
                            : isCurrent
                              ? 'bg-secondary text-primary border-primary/30 hover:bg-secondary'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                        }`}
                      >
                        <div className={`text-[10px] font-medium ${isSelected ? 'text-white' : 'text-gray-400'}`}>W{weekData.week}</div>
                        <div className={`text-[10px] font-semibold whitespace-nowrap ${isSelected ? 'text-white' : 'text-gray-700'}`}>{formatDateRange(weekData.start, weekData.end)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <button onClick={() => navigateWeek('next')} className='p-1.5 rounded-md hover:bg-gray-100 flex-shrink-0 text-gray-500' aria-label='Next week'>
                <ChevronRight className='h-4 w-4' />
              </button>
            </div>
          )}
          {filters.periodType === 'month' && (
            <div className='flex items-center gap-2'>
              <Input
                type='select'
                value={filters.month ?? now.getMonth() + 1}
                onChange={e => handleFilterChange('month', Number(e.target.value))}
                options={[1,2,3,4,5,6,7,8,9,10,11,12].map(m => ({ value: m, label: new Date(2000, m - 1, 1).toLocaleDateString('en-US', { month: 'long' }) }))}
                className='w-auto min-w-[140px] text-sm'
              />
              <Input type='number' min={2020} max={2030} value={filters.monthYear ?? now.getFullYear()} onChange={e => handleFilterChange('monthYear', Number(e.target.value) || now.getFullYear())} className='w-20 text-sm' />
            </div>
          )}
          {filters.periodType === 'dateRange' && (
            <div className='flex items-center gap-2'>
              <Input type='date' value={filters.startDate ?? ''} onChange={e => handleFilterChange('startDate', e.target.value)} className='w-[140px] text-sm' />
              <span className='text-gray-400 text-sm'>→</span>
              <Input type='date' value={filters.endDate ?? ''} onChange={e => handleFilterChange('endDate', e.target.value)} className='w-[140px] text-sm' />
            </div>
          )}
        </div>

        {/* Filter bar */}
        <div className='border-t border-gray-100 px-4 py-3 flex flex-col gap-2'>
          {/* Row 1 — where: search + geography */}
          <div className='flex flex-wrap items-center gap-2'>
            <Input
              type='text'
              placeholder={dictionary['assessments.search_placeholder_long']}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              inputPrefix={<Search className='h-4 w-4 ml-3 text-gray-400 flex-shrink-0' />}
              className='flex-1 min-w-[200px] text-sm'
            />

            <SingleLocationSelector
              key={locationResetKey}
              compact
              selectedLocationId={filters.locationId}
              onLocationChange={id => handleFilterChange('locationId', id)}
            />
          </div>

          {/* Row 2 — what/who: facility type, assessment type, conductor, questionnaire */}
          <div className='flex flex-wrap items-center gap-2'>
            <Input
              type='select'
              value={filters.facilityTypeId || ''}
              onChange={e => handleFilterChange('facilityTypeId', e.target.value as string)}
              placeholder={dictionary['common.all_types']}
              options={(facilityTypes ?? []).map(type => ({ value: type.id, label: getFacilityTypeName(type, locale) }))}
              className='w-[170px] flex-shrink-0 text-sm'
            />

            <Input
              type='select'
              value={filters.assessmentType || ''}
              onChange={e => handleFilterChange('assessmentType', (e.target.value as string) || undefined)}
              placeholder='All types'
              options={[
                { value: 'HYGIENE', label: dictionary['assessment_detail.type_hygiene'] ?? 'Hygiene' },
                { value: 'COMPLIANCE', label: dictionary['assessment_detail.type_compliance'] ?? 'Compliance' },
              ]}
              className='w-[170px] flex-shrink-0 text-sm'
            />

            <UserMultiSelect
              selectedIds={filters.userIds ?? []}
              selectedUsers={selectedConductors}
              onChange={(ids, users) => {
                handleFilterChange('userIds', ids.length ? ids : undefined);
                setSelectedConductors(users);
              }}
            />

            {filters.assessmentType !== 'COMPLIANCE' && questionnaires && questionnaires.length > 0 && (
              <Input
                type='select'
                value={filters.questionnaireId || ''}
                onChange={e => handleFilterChange('questionnaireId', e.target.value as string)}
                placeholder='All questionnaires'
                options={questionnaires.map(q => ({ value: q.id, label: q.name }))}
                className='w-[170px] flex-shrink-0 text-sm'
              />
            )}

            <div className='flex-1' />

            {hasActiveFilters && (
              <button onClick={clearFilters} className='h-[46px] px-3 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg border-2 border-red-200 flex items-center gap-1 transition-colors flex-shrink-0'>
                <X className='h-3.5 w-3.5' />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Conductor chips */}
        <ConductorChips
          users={selectedConductors}
          onRemove={(id) => {
            const next = filters.userIds?.filter(i => i !== id) ?? [];
            handleFilterChange('userIds', next.length ? next : undefined);
            setSelectedConductors(prev => prev.filter(u => u.id !== id));
          }}
        />

        {/* Summary footer */}
        <div className='border-t border-gray-100 px-4 py-2 flex items-center justify-between text-xs bg-gray-50'>
          <span className='text-gray-500'>
            {filters.periodType === 'week' && `Week ${selectedWeek}, ${selectedYear}${selectedWeekData ? ` (${formatDateRange(selectedWeekData.start, selectedWeekData.end)})` : ''}`}
            {filters.periodType === 'month' && filters.month != null && filters.monthYear != null && `${new Date(filters.monthYear, filters.month - 1, 1).toLocaleDateString('en-US', { month: 'long' })} ${filters.monthYear}`}
            {filters.periodType === 'dateRange' && filters.startDate && filters.endDate && `${filters.startDate} – ${filters.endDate}`}
          </span>
          <div className='flex items-center gap-4 font-medium'>
            <span className='text-primary'>{totalItems} assessed</span>
            <span className='text-amber-600'>{unassessedData?.unassessedCount ?? 0} unassessed</span>
          </div>
        </div>
      </div>

      <div className='border-b border-gray-200 overflow-x-auto'>
        <nav className='-mb-px flex space-x-6 min-w-max'>
          <button
            onClick={() => setActiveTab('assessed')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'assessed'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className='flex items-center gap-2'>
              <CheckCircle className='h-4 w-4' />
              <span>{dictionary['assessments.assessed_facilities']}</span>
              <span
                className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                  activeTab === 'assessed' ? 'bg-secondary text-primary' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {totalItems}
              </span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('unassessed')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'unassessed'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className='flex items-center gap-2'>
              <AlertCircle className='h-4 w-4' />
              <span>{dictionary['assessments.unassessed_facilities']}</span>
              <span
                className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                  activeTab === 'unassessed' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {unassessedData?.unassessedCount || 0}
              </span>
            </div>
          </button>
        </nav>
      </div>

      {activeTab === 'unassessed' ? (
        unassessedData && unassessedData.unassessedCount > 0 ? (
          <div className='border border-amber-200 bg-amber-50 rounded-lg shadow-sm p-4 sm:p-6'>
            <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6'>
              <div className='flex items-start gap-3'>
                <div className='p-2 bg-amber-100 rounded-lg flex-shrink-0'>
                  <AlertCircle className='h-5 w-5 text-amber-600' />
                </div>
                <div>
                  <h3 className='text-base sm:text-lg font-semibold text-amber-900'>
                    {filters.periodType === 'week' && unassessedData.weekNumber != null
                      ? dictionary['assessments.unassessed_facilities_for_week']?.replace('{week}', String(unassessedData.weekNumber)).replace('{year}', String(unassessedData.year)) ?? `Unassessed for Week ${unassessedData.weekNumber}, ${unassessedData.year}`
                      : filters.periodType === 'month' && filters.month != null && filters.monthYear != null
                        ? (dictionary['assessments.unassessed_facilities_for_month']?.replace('{month}', new Date(filters.monthYear, filters.month - 1, 1).toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-US', { month: 'long' })).replace('{year}', String(filters.monthYear)) ?? `Unassessed for ${new Date(filters.monthYear, filters.month - 1, 1).toLocaleDateString('en-US', { month: 'long' })} ${filters.monthYear}`)
                        : (dictionary['assessments.unassessed_facilities_for_range'] ?? `Unassessed for selected date range`)}
                  </h3>
                  <p className='text-sm text-amber-700 mt-1'>
                    {dictionary['assessments.facilities_need_assessment']
                      .replace('{count}', String(unassessedData.unassessedCount))
                      .replace('{total}', String(unassessedData.totalFacilities))}
                  </p>
                </div>
              </div>
              <div className='text-right flex-shrink-0'>
                <div className='text-2xl sm:text-3xl font-bold text-amber-900'>{unassessedData.unassessedCount}</div>
                <div className='text-xs text-amber-600 font-medium'>{dictionary['assessments.pending']}</div>
              </div>
            </div>

            <div className='mb-6'>
              <div className='flex justify-between text-sm mb-2'>
                <span className='text-amber-700 font-medium'>{dictionary['assessments.assessment_progress']}</span>
                <span className='text-amber-900 font-semibold'>
                  {unassessedData.assessedCount}/{unassessedData.totalFacilities} (
                  {((unassessedData.assessedCount / unassessedData.totalFacilities) * 100).toFixed(0)}%)
                </span>
              </div>
              <div className='w-full bg-amber-200 rounded-full h-3'>
                <div
                  className='bg-amber-600 h-3 rounded-full transition-all duration-500'
                  style={{ width: `${(unassessedData.assessedCount / unassessedData.totalFacilities) * 100}%` }}
                />
              </div>
            </div>

            {unassessedLoading ? (
              <div className='space-y-3'>
                <Skeleton className='h-6 w-48' />
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
                  {[...Array(unassessedItemsPerPage)].map((_, i) => (
                    <div key={i} className='bg-white border border-amber-200 rounded-lg p-4 space-y-3'>
                      {/* Title skeleton */}
                      <Skeleton className='h-5 w-full' />
                      <Skeleton className='h-4 w-3/4' />

                      {/* Facility type skeleton */}
                      <div className='flex items-center gap-1.5'>
                        <Skeleton className='h-3 w-3 rounded-full' />
                        <Skeleton className='h-4 w-32' />
                      </div>

                      {/* Location skeleton */}
                      <div className='flex items-center gap-1.5'>
                        <Skeleton className='h-3 w-3 rounded-full' />
                        <Skeleton className='h-4 w-40' />
                      </div>

                      {/* Button skeleton */}
                      <Skeleton className='h-8 w-full rounded-md' />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className='space-y-3'>
                <h4 className='text-sm font-semibold text-amber-900'>
                  {dictionary['assessments.facilities_requiring']}
                </h4>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
                  {unassessedFacilities.map(facility => (
                    <div
                      key={facility.id}
                      className='bg-white border border-amber-200 rounded-lg p-4 hover:border-amber-400 hover:shadow-md transition-all'
                    >
                      <div className='mb-3'>
                        <h5 className='font-semibold text-gray-900 text-sm mb-2 line-clamp-2'>{facility.title}</h5>
                        {facility.facilityType && (
                          <div className='flex items-center gap-1.5 text-xs text-gray-600 mb-1'>
                            <Building2 className='h-3 w-3 flex-shrink-0' />
                            <span className='truncate'>{facility.facilityType.name}</span>
                          </div>
                        )}
                        {facility.location && (
                          <div className='flex items-center gap-1.5 text-xs text-gray-600'>
                            <MapPin className='h-3 w-3 flex-shrink-0' />
                            <span className='truncate'>
                              {facility.locationStack?.locationNames?.join(' › ') ?? facility.location.name}
                            </span>
                          </div>
                        )}
                      </div>
                      {canCreateAssessment && (
                        <Link to={`/assessments/new?facilityId=${facility.id}`}>
                          <Button
                            size='sm'
                            className='w-full text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white'
                          >
                            Assess Now
                          </Button>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>

                {/* Pagination for unassessed facilities */}
                {unassessedTotalItems > 0 && (
                  <div className='mt-4'>
                    <Pagination
                      currentPage={unassessedCurrentPage}
                      totalPages={unassessedTotalPages}
                      totalItems={unassessedTotalItems}
                      itemsPerPage={unassessedItemsPerPage}
                      onPageChange={setUnassessedCurrentPage}
                      onItemsPerPageChange={limit => {
                        setUnassessedItemsPerPage(limit);
                        setUnassessedCurrentPage(1);
                      }}
                      showItemsPerPage={true}
                      itemsPerPageOptions={[10, 20, 50, 100]}
                      translations={{
                        showing: dictionary['common.showing'],
                        to: dictionary['common.to'],
                        of: dictionary['common.of'],
                        results: dictionary['common.results'],
                        itemsPerPage: dictionary['common.items_per_page'],
                        previous: dictionary['common.previous'],
                        next: dictionary['common.next'],
                        firstPage: dictionary['common.first_page'],
                        lastPage: dictionary['common.last_page'],
                        goToPage: dictionary['common.go_to_page'],
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-8'>
            <div className='text-center py-12'>
              <div className='bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center'>
                <CheckCircle className='h-8 w-8 text-green-600' />
              </div>
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>All Facilities Assessed!</h3>
              <p className='text-gray-600'>
                {filters.periodType === 'week'
                  ? `All facilities for Week ${selectedWeek}, ${selectedYear} have been assessed.`
                  : filters.periodType === 'month' && filters.month != null && filters.monthYear != null
                    ? `All facilities for ${new Date(filters.monthYear, filters.month - 1, 1).toLocaleDateString('en-US', { month: 'long' })} ${filters.monthYear} have been assessed.`
                    : 'All facilities for the selected period have been assessed.'}
              </p>
            </div>
          </div>
        )
      ) : (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6'>
          <h2 className='text-base sm:text-lg font-semibold text-gray-900 mb-4'>
            {dictionary['assessments.all_assessments']}
          </h2>

          {!isLoading && filteredAssessments.length > 0 && facilityTypePerformance.length > 0 && (
            <div className='mb-6 p-4 sm:p-6 bg-secondary rounded-lg border border-primary/30'>
              <h3 className='text-sm font-semibold text-gray-900 mb-4'>Mean Score by Facility Type</h3>
              <ResponsiveContainer width='100%' height={300}>
                <RadarChart data={facilityTypePerformance}>
                  <PolarGrid stroke='#cbd5e1' />
                  <PolarAngleAxis dataKey='facilityType' tick={{ fill: '#475569', fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 10 }} />
                  <Radar name='Score' dataKey='score' stroke='#3b82f6' fill='#3b82f6' fillOpacity={0.6} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '12px',
                      padding: '8px 12px',
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className='overflow-x-auto'>
            {isLoading ? (
              <div className='space-y-3'>
                {[...Array(itemsPerPage)].map((_, i) => (
                  <div key={i} className='flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg'>
                    {/* Facility info skeleton */}
                    <div className='flex items-center gap-3 flex-1'>
                      <Skeleton className='h-10 w-10 rounded-lg' />
                      <div className='flex-1 space-y-2'>
                        <Skeleton className='h-5 w-3/4 sm:w-1/2' />
                        <Skeleton className='h-4 w-1/2 sm:w-1/3' />
                      </div>
                    </div>

                    {/* Location skeleton - hidden on mobile */}
                    <Skeleton className='hidden md:block h-5 w-32' />

                    {/* Assessor skeleton - hidden on mobile */}
                    <Skeleton className='hidden lg:block h-5 w-28' />

                    {/* Date skeleton - hidden on mobile */}
                    <Skeleton className='hidden sm:block h-5 w-24' />

                    {/* Flag skeleton */}
                    <Skeleton className='h-8 w-8 rounded' />

                    {/* Score skeleton */}
                    <Skeleton className='h-7 w-16 rounded-lg' />
                  </div>
                ))}
              </div>
            ) : (
              <Table
                columns={tableColumns}
                data={filteredAssessments}
                isLoading={isLoading}
                enableSearch={false}
                enablePagination={false}
                emptyMessage={
                  hasActiveFilters
                    ? dictionary['assessments.no_assessments_filters']
                    : dictionary['assessments.no_assessments']
                }
                showHeader={false}
              />
            )}
          </div>

          {/* API-based Pagination */}
          {totalItems > 0 && (
            <div className='mt-4'>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={limit => {
                  setItemsPerPage(limit);
                  setCurrentPage(1); // Reset to first page when changing items per page
                }}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 20, 50, 100]}
                translations={{
                  showing: dictionary['common.showing'],
                  to: dictionary['common.to'],
                  of: dictionary['common.of'],
                  results: dictionary['common.results'],
                  itemsPerPage: dictionary['common.items_per_page'],
                  previous: dictionary['common.previous'],
                  next: dictionary['common.next'],
                  firstPage: dictionary['common.first_page'],
                  lastPage: dictionary['common.last_page'],
                  goToPage: dictionary['common.go_to_page'],
                }}
              />
            </div>
          )}
        </div>
      )}

    </div>
  );
}
